import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useRef,useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import { handleUnauthorized, showConfirmationDialog } from "../../utils/apiHelper";
import moment from "moment";
import Swal from "sweetalert2";
import { getModulePermissions } from "../../utils/permissionUtils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { reportTypeLabel, reportTypeOptions } from "./reportTypes";


const UserReportListPage = ({ userId }) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedItems, setSelectedItems] = useState([]);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [sortBy, setSortBy] = useState("createdAt"); // default sort
    const [sortOrder, setSortOrder] = useState("desc"); // default order
    const [search, setSearch] = useState("");
    const [availableReportTypes, setAvailableReportTypes] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [minReportCount, setMinReportCount] = useState("");
    const [maxReportCount, setMaxReportCount] = useState("");



    // Filters
    const [statusFilter, setStatusFilter] = useState("");
    const [severityFilter, setSeverityFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [reportTypeFilter, setReportTypeFilter] = useState("");

    const navigate = useNavigate();
    const authToken = localStorage.getItem("authToken");
    const { shouldCheckPermissions, canEdit, canAdd, canView, canDelete } =
        getModulePermissions("user-report");

    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        if (!openDropdownId) return;
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpenDropdownId(null);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [openDropdownId]);

    const toggleDropdown = useCallback((e, id) => {
        if (openDropdownId === id) {
            setOpenDropdownId(null);
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        setDropdownPos({
            top: rect.bottom + window.scrollY + 4,
            left: rect.right + window.scrollX,
        });
        setOpenDropdownId(id);
    }, [openDropdownId]);


    useEffect(() => {
        fetchData(currentPage);
    }, [currentPage, itemsPerPage, statusFilter, severityFilter, typeFilter, reportTypeFilter, search, sortBy, sortOrder, startDate,
        endDate, minReportCount,
        maxReportCount]);

    const fetchData = async (page) => {
        try {
            setLoading(true);

            let API_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/admin/user-reports?page=${page}&limit=${itemsPerPage}`;

            if (userId) API_ENDPOINT += `&userId=${userId}`;
            if (statusFilter) API_ENDPOINT += `&status=${statusFilter}`;
            if (severityFilter) API_ENDPOINT += `&severityTag=${severityFilter}`;
            if (typeFilter) API_ENDPOINT += `&type=${typeFilter}`;
            if (reportTypeFilter) API_ENDPOINT += `&reportType=${reportTypeFilter}`;
            if (search) API_ENDPOINT += `&search=${encodeURIComponent(search)}`; // <-- add this
            if (sortBy) API_ENDPOINT += `&sortBy=${sortBy}&sortOrder=${sortOrder}`; // <-- add this
            if (startDate)
                API_ENDPOINT += `&startDate=${moment(startDate).format("YYYY-MM-DD")}`;

            if (endDate)
                API_ENDPOINT += `&endDate=${moment(endDate).format("YYYY-MM-DD")}`;
            if (minReportCount)
                API_ENDPOINT += `&minReportCount=${minReportCount}`;

            if (maxReportCount)
                API_ENDPOINT += `&maxReportCount=${maxReportCount}`;
            const response = await fetch(API_ENDPOINT, {
                headers: { Authorization: `Bearer ${authToken}` },
            });

            if (response.status === 401) {
                handleUnauthorized(navigate);
                return;
            }

            if (!response.ok) throw new Error("Failed to fetch data");

            const data = await response.json();

            setReports(data.data || []);
            setTotalItems(data.pagination?.total || 0);
            setTotalPages(data.pagination?.pages || 1);
            // --- Extract unique report types dynamically ---
            const typesSet = new Set();
            (data.data || []).forEach((report) => {
                report.reportTypes?.forEach((type) => typesSet.add(type));
            });
            setAvailableReportTypes([...typesSet]);

        } catch (error) {
            setReports([]);
            setErrorMessage(error.message);
            setTimeout(() => setErrorMessage(""), 3000);
        } finally {
            setLoading(false);
        }
    };

    // Checkbox Logic
    const handleSelectAll = (e) => {
        if (e.target.checked) setSelectedItems(reports.map((item) => item._id));
        else setSelectedItems([]);
    };

    const handleSelectItem = (id) => {
        setSelectedItems((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const isSelected = (id) => selectedItems.includes(id);

    const handleDeleteReports = async (id, single = "No") => {
        const idsToDelete = single === "Yes" ? [id] : selectedItems;
        if (idsToDelete.length === 0) return;

        const reportText = `Report${idsToDelete.length > 1 ? "s" : ""}`;
        const message = `This action will delete the ${reportText}!`;

        // Dynamic confirm/cancel
        const confirmMessage = idsToDelete.length > 1 ? "Delete All" : "Delete";
        const cancelMessage = "Cancel";

        const result = await showConfirmationDialog(
            message,
            confirmMessage,
            cancelMessage
        );

        if (!result.isConfirmed) return;

        try {
            const API_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/admin/user-reports/delete`;

            const response = await fetch(API_ENDPOINT, {
                method: "POST", // ✅ POST to match backend
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({ id: idsToDelete }), // always array
            });

            if (response.status === 401) {
                handleUnauthorized(navigate);
                return;
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
            }

            const successMessage =
                single === "Yes"
                    ? "Report deleted successfully"
                    : "Reports deleted successfully";

            setSuccessMessage(successMessage);
            setTimeout(() => setSuccessMessage(""), 3000);

            if (single === "No") setSelectedItems([]);

            // Refresh the report list
            fetchData(currentPage);
        } catch (error) {
            setErrorMessage(error.message);
            setTimeout(() => setErrorMessage(""), 3000);
        }
    };



    // Pagination
    const handleNext = () => currentPage < totalPages && setCurrentPage((p) => p + 1);
    const handlePrev = () => currentPage > 1 && setCurrentPage((p) => p - 1);

    const getPaginationRange = () => {
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, start + 4);
        if (end - start < 4) start = Math.max(1, end - 4);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    };



    // Admin Action: Update Report Status
    const updateReportStatus = async (reportId, newStatus) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/admin/report/status-update/${reportId}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                    },
                    body: JSON.stringify({ status: newStatus }),
                }
            );

            const data = await response.json();
            if (!response.ok) throw new Error(data.message || "Failed to update status");

            setSuccessMessage("Status updated successfully");
            fetchData(currentPage);
        } catch (error) {
            setErrorMessage(error.message);
        }
    };



    const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "N/A");
    const formatText = (str) => {
        if (!str) return "N/A";
        return str
            .replace(/_/g, " ")
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    const handleSort = (column) => {
        setSortBy(column);
        setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    };

    const handleUserStatus = async (user, reportId) => {
        const isSuspended = user.status === "suspended";
        const actionType = isSuspended ? "activate" : "suspend";

        const message = isSuspended
            ? "Do you want to activate this suspended user?"
            : "Do you want to suspend this user?";

        const result = await showConfirmationDialog(message);
        if (!result.isConfirmed) return;

        adminUserAction(user._id, reportId, actionType);
    };

    const handleUserBan = async (user) => {
        const isBanned = user.status === "banned";
        const actionType = isBanned ? "activate" : "ban";

        const message = isBanned
            ? "Do you want to activate this banned user?"
            : "Do you want to ban this user?";

        const result = await showConfirmationDialog(message);
        if (!result.isConfirmed) return;

        adminUserAction(user._id, null, actionType);
    };


    const adminUserAction = async (userId, reportId, actionType) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/admin/user-reports/user-action/${userId}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                    },
                    body: JSON.stringify({ action: actionType, reportId }),
                }
            );

            const data = await response.json();
            if (!response.ok) throw new Error(data.message || "Action failed");

            setSuccessMessage(`User ${actionType} successfully`);
            fetchData(currentPage);
        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setTimeout(() => {
                setSuccessMessage("");
                setErrorMessage("");
            }, 3000);
        }
    };


    const MODERATOR_ACTIONS = [
        { label: "Approve Content", value: "APPROVE_CONTENT", requiresReason: false },
        { label: "Remove Content", value: "REMOVE_CONTENT", requiresReason: true },
        { label: "Clear Profile Fields", value: "CLEAR_PROFILE_FIELDS", requiresReason: true },
        { label: "Issue Warning", value: "ISSUE_WARNING", requiresReason: true },
        { label: "Suspend User", value: "SUSPEND_USER", requiresReason: true },
        { label: "Ban User", value: "BAN_USER", requiresReason: true },
        { label: "Escalate to Admin", value: "ESCALATE_TO_ADMIN", requiresReason: true },
    ];
    const STATUS_BASED_ACTIONS = {
        suspended: {
            replace: "SUSPEND_USER",
            with: {
                label: "Unsuspend User",
                value: "UNSUSPEND_USER",
                requiresReason: true,
            },
        },
        banned: {
            replace: "BAN_USER",
            with: {
                label: "Unban User",
                value: "UNBAN_USER",
                requiresReason: true,
            },
        },
    };

    const getModeratorActionsForItem = (item) => {
        const userStatus = item?.targetUserId?.status;
        let actions = [...MODERATOR_ACTIONS];

        if (userStatus === "suspended") {
            actions = actions.map((a) =>
                a.value === STATUS_BASED_ACTIONS.suspended.replace
                    ? STATUS_BASED_ACTIONS.suspended.with
                    : a
            );
        }

        if (userStatus === "banned") {
            actions = actions.map((a) =>
                a.value === STATUS_BASED_ACTIONS.banned.replace
                    ? STATUS_BASED_ACTIONS.banned.with
                    : a
            );
        }

        return actions;
    };


    const isActionDisabled = (action, item) => {
        const userStatus = item.targetUserId?.status;

        if (action === "SUSPEND_USER" && userStatus === "suspended") return true;
        if (action === "UNSUSPEND_USER" && userStatus !== "suspended") return true;

        if (action === "BAN_USER" && userStatus === "banned") return true;
        if (action === "UNBAN_USER" && userStatus !== "banned") return true;

        if (action === "APPROVE_CONTENT" && item.status === "reviewed") return true;
        if (action === "ESCALATE_TO_ADMIN" && item.status === "escalated") return true;

        return false;
    };


    const performModeratorAction = async ({
        reportId,
        targetUserId,
        action,
        reason,
        metadata = {},
    }) => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BASE_URL}/admin/user-reports/${reportId}/moderator-action`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                    },
                    body: JSON.stringify({
                        targetUserId,
                        action,
                        reason,
                        metadata,
                    }),
                }
            );

            const data = await res.json();
            if (!res.ok) throw new Error(data.message);

            setSuccessMessage("Moderator action completed successfully");
            fetchData(currentPage);
        } catch (err) {
            setErrorMessage(err.message);
        }
        finally {
            setTimeout(() => {
                setSuccessMessage("");
                setErrorMessage("");
            }, 3000);
        }
    };

    const handleModeratorAction = async (actionConfig, item) => {
        let reason = "";

        // Reason modal (required)
        if (actionConfig.requiresReason) {
            const { value } = await Swal.fire({
                title: actionConfig.label,
                input: "textarea",
                inputLabel: "Reason (required)",
                inputPlaceholder: "Enter reason...",
                inputValidator: (value) => {
                    if (!value) return "Reason is required";
                },
                showCancelButton: true,
            });

            if (!value) return;
            reason = value;
        }

        // Optional: confirm destructive actions
        const destructiveActions = [
            "REMOVE_CONTENT",
            "BAN_USER",
            "CLEAR_PROFILE_FIELDS",
        ];

        if (destructiveActions.includes(actionConfig.value)) {
            const confirm = await Swal.fire({
                title: "Are you sure?",
                text: "This action cannot be easily undone.",
                icon: "warning",
                showCancelButton: true,
            });

            if (!confirm.isConfirmed) return;
        }

        // Final API call
        performModeratorAction({
            reportId: item._id,
            targetUserId: item.targetUserId?._id,
            action: actionConfig.value,
            reason,
        });
    };

    return (
        <div className="card h-100 p-0 radius-12">
            {successMessage && (
                <SuccessAlert message={successMessage} onClose={() => setSuccessMessage("")} />
            )}
            {errorMessage && (
                <DengerAlert message={errorMessage} onClose={() => setErrorMessage("")} />
            )}

            {/* Header Filters */}
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center flex-wrap gap-2">
                    <span className="text-md fw-medium text-secondary-light mb-0">Show</span>

                    <select
                        className="form-select form-select-sm w-auto ps-12 py-6"
                        style={{
                            fontSize: "16px",
                            borderRadius: "7px",
                            backgroundPositionX: "unset",
                            backgroundPosition: "right 0.75rem center",
                        }}
                        value={itemsPerPage}
                        onChange={(e) => setItemsPerPage(parseInt(e.target.value, 10))}
                    >
                        <option value="10">10 items per page</option>
                        <option value="20">20 items per page</option>
                        <option value="50">50 items per page</option>
                        <option value="100">100 items per page</option>
                    </select>

                    {/* Search */}
                    <form className="navbar-search" style={{ position: "relative" }}>
                        <input
                            type="text"
                            className="bg-base h-40-px w-auto pe-40"
                            placeholder="Search"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setCurrentPage(1);
                            }}
                            style={{
                                paddingRight: search ? "5rem" : "2.5rem",
                                borderRadius: "7px",
                            }}
                        />

                        {/* Search icon */}
                        <Icon
                            icon="ion:search-outline"
                            className="icon"
                            style={{
                                position: "absolute",
                                right: search ? "3.25rem" : "1rem",
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: "1.25rem",
                                color: "#999",
                                pointerEvents: "none",
                            }}
                        />

                        {/* Clear button */}
                        {search && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSearch("");
                                    setCurrentPage(1);
                                }}
                                style={{
                                    position: "absolute",
                                    right: "1rem",
                                    top: "30%",
                                    transform: "translateY(-50%)",
                                    fontSize: "1.25rem",
                                    color: "#000",
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                }}
                            >
                                <Icon icon="ic:round-close" />
                            </button>
                        )}
                    </form>

                    {/* Start Date */}
                    <div style={{ position: "relative", maxWidth: "200px" }}>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => {
                                setStartDate(date);
                                setCurrentPage(1);
                            }}
                            className="form-control h-40-px"
                            placeholderText="Start Date"
                            dateFormat="yyyy-MM-dd"
                            maxDate={endDate || new Date()}
                        />

                        {startDate && (
                            <button
                                type="button"
                                onClick={() => {
                                    setStartDate(null);
                                    setCurrentPage(1);
                                }}
                                className="date-clear-btn"
                                style={{
                                    position: "absolute",
                                    right: "10px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    background: "transparent",
                                    border: "none",
                                    fontSize: "1.25rem",
                                    color: "#000",
                                    cursor: "pointer",
                                    display: "flex",
                                }}
                            >
                                <Icon icon="ic:round-close" />
                            </button>
                        )}
                    </div>

                    {/* End Date */}
                    <div style={{ position: "relative", maxWidth: "200px" }}>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => {
                                setEndDate(date);
                                setCurrentPage(1);
                            }}
                            className="form-control h-40-px"
                            placeholderText="End Date"
                            dateFormat="yyyy-MM-dd"
                            minDate={startDate}
                            maxDate={new Date()}
                        />

                        {endDate && (
                            <button
                                type="button"
                                onClick={() => {
                                    setEndDate(null);
                                    setCurrentPage(1);
                                }}
                                className="date-clear-btn"
                                style={{
                                    position: "absolute",
                                    right: "10px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    background: "transparent",
                                    border: "none",
                                    fontSize: "1.25rem",
                                    color: "#000",
                                    cursor: "pointer",
                                    display: "flex",
                                }}
                            >
                                <Icon icon="ic:round-close" />
                            </button>
                        )}
                    </div>

                    {/* Report Count Filter */}
                    {/* <div className="d-flex align-items-center gap-2">
                        <input
                            type="number"
                            min="0"
                            className="form-control h-40-px"
                            placeholder="Min Reports"
                            value={minReportCount}
                            onChange={(e) => {
                                setMinReportCount(e.target.value);
                                setCurrentPage(1);
                            }}
                            style={{ width: "120px" }}
                        />

                        <span className="fw-bold">–</span>

                        <input
                            type="number"
                            min="0"
                            className="form-control h-40-px"
                            placeholder="Max Reports"
                            value={maxReportCount}
                            onChange={(e) => {
                                setMaxReportCount(e.target.value);
                                setCurrentPage(1);
                            }}
                            style={{ width: "120px" }}
                        />
                    </div> */}

                    {/* Status */}
                    <select
                        className="form-select form-select-sm w-auto ps-12 py-6"
                        style={{
                            fontSize: "16px",
                            borderRadius: "7px",
                            backgroundPositionX: "unset",
                            backgroundPosition: "right 0.75rem center",
                        }}
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">All Status</option>
                        <option value="open">Open</option>
                        <option value="pending">Pending</option>
                        <option value="reviewed">Reviewed</option>
                        <option value="action_taken">Action Taken</option>
                        <option value="warning_issued">Warning Issued</option>
                        <option value="content_removed">Content Removed</option>
                        <option value="profile_cleared">Profile Cleared</option>
                        <option value="escalated">Escalated</option>

                    </select>

                    {/* Severity */}
                    <select
                        className="form-select form-select-sm w-auto ps-12 py-6"
                        style={{
                            fontSize: "16px",
                            borderRadius: "7px",
                            backgroundPositionX: "unset",
                            backgroundPosition: "right 0.75rem center",
                        }}
                        value={severityFilter}
                        onChange={(e) => setSeverityFilter(e.target.value)}
                    >
                        <option value="">All Severity</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                    </select>

                    {/* Report Type */}
                    <select
                        className="form-select form-select-sm w-auto ps-12 py-6"
                        style={{
                            fontSize: "16px",
                            borderRadius: "7px",
                            backgroundPositionX: "unset",
                            backgroundPosition: "right 0.75rem center",
                        }}
                        value={reportTypeFilter}
                        onChange={(e) => setReportTypeFilter(e.target.value)}
                    >
                        <option value="">All Report Types</option>
                        {reportTypeOptions(availableReportTypes).map((t) => (
                            <option key={t.value} value={t.value}>
                                {t.label}
                            </option>
                        ))}
                    </select>

                    {(!shouldCheckPermissions || canDelete) && (
                        <button
                            className="btn btn-danger"
                            onClick={() => handleDeleteReports()}
                            disabled={selectedItems.length === 0}
                        >
                            Delete All
                        </button>
                    )}
                </div>
            </div>

            {/* TABLE */}
            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col">
                                    <div className="d-flex align-items-center gap-10">
                                        <div className="form-check style-check d-flex align-items-center">
                                            <input
                                                className="form-check-input radius-4 border input-form-dark"
                                                type="checkbox"
                                                name="checkbox"
                                                id="selectAll"
                                                onChange={handleSelectAll}
                                                checked={
                                                    selectedItems.length === reports.length &&
                                                    reports.length > 0
                                                }
                                            />
                                        </div>
                                    </div>
                                </th>
                                <th
                                    onClick={() => handleSort("ownerUserId.email")}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="d-flex align-items-center gap-10">
                                        Reporter
                                        <span className="sort-colume">
                                            <Icon
                                                icon="mdi:arrow-up"
                                                className={`arrow ${sortBy === "ownerUserId.email" && sortOrder === "asc"
                                                    ? "active"
                                                    : ""
                                                    }`}
                                            />
                                            <Icon
                                                icon="mdi:arrow-down"
                                                className={`arrow ${sortBy === "ownerUserId.email" && sortOrder === "desc"
                                                    ? "active"
                                                    : ""
                                                    }`}
                                            />
                                        </span>
                                    </div>
                                </th>

                                <th
                                    onClick={() => handleSort("targetUserId.email")}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="d-flex align-items-center gap-10">
                                        Reported User
                                        <span className="sort-colume">
                                            <Icon
                                                icon="mdi:arrow-up"
                                                className={`arrow ${sortBy === "targetUserId.email" && sortOrder === "asc"
                                                    ? "active"
                                                    : ""
                                                    }`}
                                            />
                                            <Icon
                                                icon="mdi:arrow-down"
                                                className={`arrow ${sortBy === "targetUserId.email" && sortOrder === "desc"
                                                    ? "active"
                                                    : ""
                                                    }`}
                                            />
                                        </span>
                                    </div>
                                </th>
                                <th>Reports Against User</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Severity</th>
                                <th
                                    onClick={() => handleSort("createdAt")}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="d-flex align-items-center gap-10">
                                        Date
                                        <span className="sort-colume">
                                            <Icon
                                                icon="mdi:arrow-up"
                                                className={`arrow ${sortBy === "createdAt" && sortOrder === "asc"
                                                    ? "active"
                                                    : ""
                                                    }`}
                                            />
                                            <Icon
                                                icon="mdi:arrow-down"
                                                className={`arrow ${sortBy === "createdAt" && sortOrder === "desc"
                                                    ? "active"
                                                    : ""
                                                    }`}
                                            />
                                        </span>
                                    </div>
                                </th>
                                <th>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className="text-center">
                                        <span className="loader-15"></span>
                                    </td>
                                </tr>
                            ) : reports.length > 0 ? (
                                reports.map((item) => (
                                    <tr key={item._id}>


                                        <td>
                                            <div className="d-flex align-items-center gap-10">
                                                <div className="form-check style-check d-flex align-items-center">
                                                    <input
                                                        className="form-check-input radius-4 border border-neutral-400"
                                                        type="checkbox"
                                                        name="checkbox"
                                                        value={item._id}
                                                        checked={isSelected(item._id)}
                                                        onChange={() => handleSelectItem(item._id)}
                                                    />
                                                </div>
                                            </div>
                                        </td>

                                        <td>
                                            {item.ownerUserId?.email ||
                                                item.ownerUserId?.fullName ||
                                                "N/A"}
                                        </td>

                                        <td>
                                            {item.targetUserId?.email ||
                                                item.targetUserId?.fullName ||
                                                "N/A"}
                                        </td>
                                        <td className="text-center">{item.reportCountAgainstUser}</td>

                                        <td >
                                            {item.reportTypes?.length ? (
                                                <>
                                                    {reportTypeLabel(item.reportTypes[0])}
                                                    {item.reportTypes.length > 1 && (
                                                        <span
                                                            className="text-primary ms-1 more-link"
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => navigate(`/user-report/view/${item._id}`)}
                                                            title="View full report"        >
                                                            +{item.reportTypes.length - 1} more
                                                        </span>
                                                    )}
                                                </>
                                            ) : "N/A"}
                                        </td>
                                        <td>{formatText(item.status)}</td>

                                        <td>{capitalize(item.severityTag)}</td>

                                        <td>
                                            {moment(item.createdAt).format(
                                                "DD-MM-YYYY"
                                            )}
                                        </td>

                                        <td className="d-flex gap-2">

                                            {/* {(!shouldCheckPermissions || canEdit) && (
                                                <>
                                                    <button
                                                        onClick={() => handleUserStatus(item.targetUserId, item._id)}
                                                        className={`btn btn-sm ${item.targetUserId?.status === "suspended"
                                                            ? "btn-success"
                                                            : "btn-danger"
                                                            } d-flex align-items-center gap-1`}
                                                    >
                                                        <Icon icon="mdi:account-cancel" />
                                                        {item.targetUserId?.status === "suspended" ? "Unsuspend" : "Suspend"}
                                                    </button>

                                                    <button
                                                        onClick={() => handleUserBan(item.targetUserId)}
                                                        className={`btn btn-sm ${item.targetUserId?.status === "banned"
                                                            ? "btn-success"
                                                            : "btn-dark"
                                                            } d-flex align-items-center gap-1`}
                                                    >
                                                        <Icon icon="mdi:account-lock" />
                                                        {item.targetUserId?.status === "banned" ? "Unban" : "Ban"}
                                                    </button>

                                                </>
                                            )} */}

                                            {/* {(!shouldCheckPermissions || canEdit) && (
                                                <div className="dropdown">
                                                    <button
                                                        className="btn btn-sm btn-outline-primary dropdown-toggle"
                                                        data-bs-toggle="dropdown"
                                                        data-bs-display="static"
                                                        data-bs-container="body"
                                                    >
                                                        Actions
                                                    </button>

                                                    <ul className="dropdown-menu">
                                                        {getModeratorActionsForItem(item).map((action) => (
                                                            <li key={action.value}>
                                                                <button
                                                                    className="dropdown-item"
                                                                    onClick={() => handleModeratorAction(action, item)}
                                                                    disabled={isActionDisabled(action.value, item)}
                                                                >
                                                                    {action.label}
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                              

                                            )} */}

                                            {(!shouldCheckPermissions || canEdit) && (
                                                <div style={{ position: "relative" }}>
                                                    <button
                                                        className="btn btn-sm btn-outline-primary dropdown-toggle"
                                                        onClick={(e) => toggleDropdown(e, item._id)}
                                                    >
                                                        Actions
                                                    </button>

                                                    {openDropdownId === item._id && (
                                                        <ul
                                                            ref={dropdownRef}
                                                            className="dropdown-menu show"
                                                            style={{
                                                                position: "fixed",
                                                                top: dropdownPos.top,
                                                                left: dropdownPos.left,
                                                                transform: "translateX(-100%)",
                                                                zIndex: 9999,
                                                                minWidth: "200px",
                                                            }}
                                                        >
                                                            {getModeratorActionsForItem(item).map((action) => (
                                                                <li key={action.value}>
                                                                    <button
                                                                        className="dropdown-item"
                                                                        onClick={() => {
                                                                            setOpenDropdownId(null);
                                                                            handleModeratorAction(action, item);
                                                                        }}
                                                                        disabled={isActionDisabled(action.value, item)}
                                                                    >
                                                                        {action.label}
                                                                    </button>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            )}
                                            {(!shouldCheckPermissions || canView) && (
                                                <button
                                                    title="View"
                                                    onClick={() =>
                                                        navigate(`/user-report/view/${item._id}`)
                                                    }
                                                    className="bg-info-focus text-info-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                >
                                                    <Icon icon="majesticons:eye-line" />
                                                </button>
                                            )}
                                            {(!shouldCheckPermissions || canDelete) && (
                                                <button
                                                    type="button"
                                                    title="Delete"
                                                    className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    onClick={() => handleDeleteReports(item._id, "Yes")}
                                                >
                                                    <Icon
                                                        icon="fluent:delete-24-regular"
                                                        className="menu-icon"
                                                    />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8" className="text-center">
                                        No Data Found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>


                {/* Pagination Footer */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                    <span>
                        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                    </span>

                    <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <Link className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                to="#" onClick={handlePrev}>
                                <Icon icon="ep:d-arrow-left" />
                            </Link>
                        </li>

                        {getPaginationRange().map((page) => (
                            <li key={page} className="page-item">
                                <Link
                                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === page
                                        ? "bg-primary-600 text-white"
                                        : "bg-neutral-200"
                                        }`}
                                    to="#"
                                    onClick={() => setCurrentPage(page)}
                                >
                                    {page}
                                </Link>
                            </li>
                        ))}

                        <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""
                                }`}
                        >
                            <Link className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                to="#" onClick={handleNext}>
                                <Icon icon="ep:d-arrow-right" />
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default UserReportListPage;
